import { useEffect, useRef, useState } from 'react';

function formatHms(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * «02:40:00» до `deadline` — тикает раз в секунду, не уходит в минус.
 *
 * `onExpire` зовётся ровно один раз, в момент пересечения нуля — таймер
 * вопроса Дерби истёк, значит защитан сервером как неверный ответ,
 * и экран обязан подтянуть свежее состояние (см. DerbyQuiz). Без единого
 * вызова эффект зовущий `onExpire` дёргался бы каждую секунду, пока
 * `deadline` не сменится на следующий вопрос.
 *
 * Кроме текста отдаёт и остаток в миллисекундах — по нему кольцо-таймер
 * считает, какая доля обода ещё розовая.
 */
export function useCountdown(
  deadline: string,
  onExpire?: () => void,
): { text: string; remainingMs: number } {
  const target = new Date(deadline).getTime();
  const [now, setNow] = useState(() => Date.now());
  const firedFor = useRef<string | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (now < target) return;
    if (firedFor.current === deadline) return;
    firedFor.current = deadline;
    onExpire?.();
  }, [now, target, deadline, onExpire]);

  return {
    text: formatHms(target - now),
    remainingMs: Math.max(0, target - now),
  };
}
